import { strToU8, zipSync, zlibSync } from 'fflate';
import { v5 as uuidv5 } from 'uuid';

/**
 * The Microsoft 365 Copilot agent package for the sandbox (Connections › Organization AI connections, admins only): a
 * declarative agent with one MCP plugin that points at the sandbox MCP server, signing people in through the Teams
 * developer portal's OAuth client registration (OAuthPluginVault, `reference_id`). Built in the browser: an admin
 * downloads the zip and uploads it in the Microsoft 365 admin center (Agents).
 *
 * Schema versions (checked against Microsoft Learn, 2026-10):
 * - app manifest 1.24 (copilotAgents needs 1.19 or later):
 *   https://learn.microsoft.com/microsoft-365/extensibility/schema/root-copilot-agents
 * - declarative agent v1.6: https://learn.microsoft.com/microsoft-365-copilot/extensibility/declarative-agent-manifest-1.6
 * - plugin manifest v2.4, the first with the `RemoteMCPServer` runtime:
 *   https://learn.microsoft.com/microsoft-365/copilot/extensibility/plugin-manifest-2.4
 * - dynamic tool discovery (empty `functions`, `run_for_functions: ["*"]`):
 *   https://learn.microsoft.com/microsoft-365/copilot/extensibility/plugin-dynamic-tool-discovery
 */
export const M365_SCHEMAS = {
  app: { version: '1.24', url: 'https://developer.microsoft.com/json-schemas/teams/v1.24/MicrosoftTeams.schema.json' },
  agent: { version: 'v1.6', url: 'https://developer.microsoft.com/json-schemas/copilot/declarative-agent/v1.6/schema.json' },
  plugin: { version: 'v2.4', url: 'https://developer.microsoft.com/json-schemas/copilot/plugin/v2.4/schema.json' },
};

export const M365_PACKAGE_FILES = ['manifest.json', 'declarativeAgent.json', 'ai-plugin.json', 'color.png', 'outline.png'];
export const M365_AGENT_NAME = 'Mia-Platform Flow';
export const M365_ACCENT = '#6366F1';

/** The app ID: the same for the same sandbox URL, so a new download updates the agent instead of adding another. */
export function agentAppId(mcpUrl) {
  return uuidv5(String(mcpUrl), uuidv5.URL);
}

/** A version newer at every download (an update must raise it): 1.<yyyymmdd>.<hhmm>, UTC. */
export function packageVersion(now = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  const day = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}`;
  return `1.${Number(day)}.${Number(`${now.getUTCHours()}${pad(now.getUTCMinutes())}`)}`;
}

/** The three manifests, as objects. `mcpUrl`: the sandbox MCP server; `registrationId`: the OAuth client registration ID. */
export function agentManifests({ mcpUrl, registrationId, now = new Date() }) {
  const id = String(registrationId || '').trim();
  if (!id) { throw new Error('The OAuth client registration ID is missing.'); }
  const site = new URL(mcpUrl).origin;
  const manifest = {
    $schema: M365_SCHEMAS.app.url,
    manifestVersion: M365_SCHEMAS.app.version,
    version: packageVersion(now),
    id: agentAppId(mcpUrl),
    developer: { name: 'Mia-Platform', websiteUrl: site, privacyUrl: site, termsOfUseUrl: site },
    icons: { color: 'color.png', outline: 'outline.png' },
    name: { short: M365_AGENT_NAME, full: `${M365_AGENT_NAME} sandbox` },
    description: {
      short: 'Build and preview apps in your Mia-Platform Flow sandbox.',
      full: 'Create projects, change their code, run them and share preview links in your own Mia-Platform Flow sandbox, from Microsoft 365 Copilot. You sign in with your company account the first time.',
    },
    accentColor: M365_ACCENT,
    copilotAgents: { declarativeAgents: [{ id: 'miaFlowSandbox', file: 'declarativeAgent.json' }] },
  };
  const agent = {
    $schema: M365_SCHEMAS.agent.url,
    version: M365_SCHEMAS.agent.version,
    name: M365_AGENT_NAME,
    description: 'Builds, changes and previews apps in your Mia-Platform Flow sandbox.',
    instructions: [
      'You work in the person\'s Mia-Platform Flow sandbox through the mia-platform-flow tools.',
      'Start with sandbox_list_projects, then open a project with sandbox_open_project, or create one with sandbox_create_project.',
      'Code lives under services/<name>/ in the open project. Every write is recorded, so vfs_history and vfs_rollback can undo a change.',
      'To show a running app, call vfs_get_preview_url and give the person the link. Check the preview before saying it works.',
      'Never ask for passwords or secrets in the chat.',
    ].join('\n'),
    conversation_starters: [
      { title: 'New app', text: 'Create a simple hello yellow app in my Flow sandbox' },
      { title: 'My projects', text: 'Which projects are in my Flow sandbox?' },
    ],
    actions: [{ id: 'miaSandbox', file: 'ai-plugin.json' }],
  };
  const plugin = {
    $schema: M365_SCHEMAS.plugin.url,
    schema_version: M365_SCHEMAS.plugin.version,
    name_for_human: M365_AGENT_NAME,
    namespace: 'miasandbox',
    description_for_human: 'Your Mia-Platform Flow sandbox: projects, code, previews.',
    description_for_model: 'Tools for the person\'s Mia-Platform Flow sandbox: list, open and create projects, read and write their files, run commands, and start previews with a shareable link.',
    functions: [],
    runtimes: [{
      type: 'RemoteMCPServer',
      auth: { type: 'OAuthPluginVault', reference_id: id },
      spec: { url: mcpUrl },
      run_for_functions: ['*'],
    }],
  };
  return { manifest, agent, plugin };
}

// ---- PNG icons, drawn here (no canvas): a few white bars, the "flow" mark ----

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) { c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1; }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xFFFFFFFF;
  for (const byte of bytes) { crc = CRC_TABLE[(crc ^ byte) & 0xFF] ^ (crc >>> 8); }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function chunk(type, data) {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  out.set(strToU8(type), 4);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
}

/** An RGBA PNG of `size` × `size`, each pixel from `pixel(x, y)` → [r, g, b, a]. */
export function encodePng(size, pixel) {
  const raw = new Uint8Array(size * (size * 4 + 1));
  for (let y = 0; y < size; y += 1) {
    const row = y * (size * 4 + 1);
    for (let x = 0; x < size; x += 1) { raw.set(pixel(x, y), row + 1 + x * 4); }
  }
  const header = new Uint8Array(13);
  const view = new DataView(header.buffer);
  view.setUint32(0, size);
  view.setUint32(4, size);
  header.set([8, 6, 0, 0, 0], 8); // 8-bit RGBA, no interlace
  const parts = [new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]), chunk('IHDR', header), chunk('IDAT', zlibSync(raw)), chunk('IEND', new Uint8Array())];
  const png = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let at = 0;
  for (const part of parts) { png.set(part, at); at += part.length; }
  return png;
}

// Three rounded bars, shorter to the right, in a unit square (fractions of the icon).
const BARS = [[0.22, 0.3, 0.78], [0.22, 0.47, 0.62], [0.22, 0.64, 0.46]];
function onMark(u, v) {
  const half = 0.06;
  return BARS.some(([x0, y, x1]) => {
    const dy = Math.abs(v - y);
    if (dy > half) { return false; }
    if (u >= x0 && u <= x1) { return true; }
    const dx = u < x0 ? x0 - u : u - x1;
    return dx * dx + dy * dy <= half * half;
  });
}

const hex = (value) => [1, 3, 5].map((at) => parseInt(value.slice(at, at + 2), 16));

/** The 192 × 192 full-colour icon: the accent colour with the white mark. */
export function colorIcon() {
  const size = 192;
  const accent = hex(M365_ACCENT);
  return encodePng(size, (x, y) => (onMark((x + 0.5) / size, (y + 0.5) / size) ? [255, 255, 255, 255] : [...accent, 255]));
}

/** The 32 × 32 outline icon: white on transparent only, as Microsoft requires. */
export function outlineIcon() {
  const size = 32;
  return encodePng(size, (x, y) => (onMark((x + 0.5) / size, (y + 0.5) / size) ? [255, 255, 255, 255] : [0, 0, 0, 0]));
}

/** The agent package (a zip) and its manifests: `{ zip: Uint8Array, manifests }`. */
export function buildAgentPackage(options) {
  const manifests = agentManifests(options);
  const json = (value) => strToU8(`${JSON.stringify(value, null, 2)}\n`);
  const zip = zipSync({
    'manifest.json': json(manifests.manifest),
    'declarativeAgent.json': json(manifests.agent),
    'ai-plugin.json': json(manifests.plugin),
    'color.png': colorIcon(),
    'outline.png': outlineIcon(),
  });
  return { zip, manifests };
}

/** Saves the package as mia-platform-flow-m365-agent.zip. */
export function downloadAgentPackage(options) {
  const { zip } = buildAgentPackage(options);
  const url = URL.createObjectURL(new Blob([zip], { type: 'application/zip' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'mia-platform-flow-m365-agent.zip';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
